import { useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  type IssuedApiAccessToken,
  createApiToken,
  listApiTokens,
  revokeApiToken,
} from '../../api/portal'
import { DataState } from '../../components/DataState'
import { QueryError } from '../../components/QueryError'
import { ConfirmAction } from '../../components/ui/ConfirmAction'
import { StatusChip } from '../../components/ui/StatusChip'
import { formatDateTime } from '../../format'
import { can } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { Ltr } from '../../components/ui/Ltr'

/**
 * The credential another product is configured with.
 *
 * Deliberately separate from the client secret above it, and labelled so the
 * difference is legible without reading documentation: a client id and secret
 * are for software we wrote, which can hold two values and exchange them; this
 * is one value for a person to paste into somebody else's settings screen.
 *
 * The value is shown once. Everything about this screen assumes the reader
 * will paste it somewhere immediately and never see it again — which is why
 * the username is displayed beside it, since they need both and only one of
 * them is a secret.
 */
export function ApiTokensSection({
  customerId,
  applicationId,
  /** Service customers meet this section on their way to pasting two values
   *  into somebody else's settings screen. The machinery is identical; the
   *  words around it are not. */
  simple = false,
}: {
  customerId: string
  applicationId: string
  simple?: boolean
}) {
  const { membership } = usePortal()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [issued, setIssued] = useState<IssuedApiAccessToken | null>(null)
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null)
  const [rotating, setRotating] = useState<{ id: string; name: string } | null>(null)

  const mayCreate = can(membership, 'create_credential')
  const mayRevoke = can(membership, 'revoke_credential')

  const tokensKey = ['client', 'api-tokens', applicationId]
  const tokens = useQuery({
    queryKey: tokensKey,
    queryFn: ({ signal }) => listApiTokens(customerId, applicationId, signal),
  })
  // Normalised rather than trusted: a list endpoint that ever answered with a
  // page envelope instead of an array would otherwise take the whole
  // application page down with it, and this section is not worth that.
  const rows = Array.isArray(tokens.data) ? tokens.data : []
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: tokensKey })
  }

  const create = useMutation({
    mutationFn: () => createApiToken(customerId, applicationId, name.trim()),
    onSuccess: (result) => {
      setIssued(result)
      setName('')
      refresh()
    },
  })

  const revoke = useMutation({
    mutationFn: (tokenId: string) => revokeApiToken(customerId, applicationId, tokenId),
    onSuccess: () => {
      setRevoking(null)
      refresh()
    },
  })

  /**
   * Replace a token whose value has been lost or leaked.
   *
   * Issue first, revoke second, and never the other way round. Revoking first
   * would leave the customer's product with no working credential for the
   * seconds in between — and if the issue then failed, indefinitely. This
   * order costs at worst a brief second live token, which the customer can
   * revoke; the other order costs an outage.
   */
  const rotate = useMutation({
    mutationFn: async (previous: { id: string; name: string }) => {
      const replacement = await createApiToken(
        customerId,
        applicationId,
        `${previous.name || 'توکن'} (جایگزین)`,
      )
      await revokeApiToken(customerId, applicationId, previous.id)
      return replacement
    },
    onSuccess: (result) => {
      setIssued(result)
      setRotating(null)
      refresh()
    },
  })

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Typography variant="h2" gutterBottom>
        {simple ? 'توکن API' : 'توکن‌های دسترسی API'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {simple
          ? 'توکن، رمز اتصال نرم‌افزار شما به سامانه است. آن را همراه نام کاربری بالا در تنظیمات نرم‌افزار خود وارد کنید. توکن فقط یک‌بار نمایش داده می‌شود.'
          : 'برای نرم‌افزارهای دیگری که می‌خواهند به این اپلیکیشن وصل شوند. در آن نرم‌افزار فقط دو چیز وارد می‌کنید: نام کاربری و همین توکن. با «اعتبارنامه‌ها»ی بالا اشتباه نگیرید؛ آن‌ها شناسه و رمز برای کتابخانهٔ رسمی‌اند و تبادل توکن لازم دارند.'}
      </Typography>


      {mayCreate ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            size="small"
            label={simple ? 'این توکن برای کدام نرم‌افزار است؟' : 'این توکن برای چیست؟'}
            value={name}
            onChange={(event) => setName(event.target.value)}
            helperText="مثلاً: سامانهٔ حسابداری"
            sx={{ minWidth: 260 }}
          />
          <Box>
            <Button
              variant="contained"
              disabled={!name.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              {simple ? 'دریافت توکن API' : 'ساخت توکن'}
            </Button>
          </Box>
        </Stack>
      ) : null}
      {create.isError ? <QueryError error={create.error} /> : null}

      <DataState
        isPending={tokens.isPending}
        isError={tokens.isError}
        error={tokens.error}
        onRetry={() => void tokens.refetch()}
      >
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            هنوز توکنی ساخته نشده است.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>نام</TableCell>
                  <TableCell>شناسه</TableCell>
                  <TableCell>وضعیت</TableCell>
                  <TableCell>ساخت</TableCell>
                  <TableCell>آخرین استفاده</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell>{token.name}</TableCell>
                    <TableCell>
                      <Ltr variant="body2"
                        style={{ textAlign: 'left' }}
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {token.token_prefix}…
                      </Ltr>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={token.status} />
                    </TableCell>
                    <TableCell>{formatDateTime(token.created_at)}</TableCell>
                    <TableCell>
                      {token.last_used_at ? formatDateTime(token.last_used_at) : 'استفاده نشده'}
                    </TableCell>
                    <TableCell align="left">
                      {token.status === 'active' ? (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                          {mayCreate ? (
                            <Button
                              size="small"
                              onClick={() => setRotating({ id: token.id, name: token.name })}
                            >
                              تعویض توکن
                            </Button>
                          ) : null}
                          {mayRevoke ? (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setRevoking({ id: token.id, name: token.name })}
                            >
                              لغو توکن
                            </Button>
                          ) : null}
                        </Stack>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataState>

      <NewTokenDialog issued={issued} onClose={() => setIssued(null)} />

      {/* Rotation issues the replacement *before* revoking the old token, so
          there is no moment with no working credential. Saying so matters:
          somebody who believes this will cut them off will avoid doing it,
          which is how a leaked token stays live. */}
      <ConfirmAction
        open={rotating !== null}
        title="توکن تازه‌ای ساخته شود؟"
        consequence={`توکن «${rotating?.name ?? ''}» پس از ساخت توکن تازه باطل می‌شود.`}
        reassurance="ابتدا توکن تازه ساخته و یک‌بار نمایش داده می‌شود، سپس توکن قبلی باطل می‌گردد؛ بنابراین وقفه‌ای پیش نمی‌آید."
        reversible
        confirmLabel="ساخت و جایگزینی"
        busy={rotate.isPending}
        onCancel={() => setRotating(null)}
        onConfirm={() => rotating && rotate.mutate(rotating)}
      />

      <ConfirmAction
        open={revoking !== null}
        title="این توکن باطل شود؟"
        consequence="هر نرم‌افزاری که با این توکن وصل شده است، از همان درخواست بعدی رد می‌شود."
        reversible={false}
        destructive
        confirmLabel="ابطال"
        busy={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
      />
    </Paper>
  )
}

/**
 * The one moment the token exists outside our database.
 *
 * It is held in this component's props and nowhere else — not in storage, not
 * in the URL, not in a console line. Closing the dialog ends the only chance
 * to copy it, and the wording says so before the reader closes it.
 */
function NewTokenDialog({
  issued,
  onClose,
}: {
  issued: IssuedApiAccessToken | null
  onClose: () => void
}) {
  // Which of the two was copied, not merely that something was. A single flag
  // made both buttons say «کپی شد» when one of them had been pressed, which is
  // exactly the confusion this screen cannot afford.
  const [copied, setCopied] = useState<'username' | 'token' | null>(null)
  if (!issued) return null

  const copy = async (value: string, which: 'username' | 'token') => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(which)
      window.setTimeout(() => setCopied(null), 1500)
    } catch {
      // Clipboard access can be refused; the value is on screen and selectable.
    }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>توکن ساخته شد</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>همین حالا کپی کنید</AlertTitle>
          این توکن را در نرم‌افزار مصرف‌کننده وارد کنید و بعداً قابل مشاهدهٔ مجدد نیست. اگر گمش
          کردید، توکن تازه‌ای بسازید و این یکی را باطل کنید.
        </Alert>

        <Typography variant="subtitle2" gutterBottom>
          نام کاربری
        </Typography>
        <Box
          component="pre"
          dir="ltr"
          style={{ textAlign: 'left' }}
          sx={{ m: 0, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto' }}
        >
          {issued.username}
        </Box>
        {/* Both values need copying, and a customer pasting them into somebody
            else's settings screen should not have to select text by hand. */}
        <Button size="small" sx={{ mb: 2 }} onClick={() => void copy(issued.username, 'username')}>
          {copied === 'username' ? 'کپی شد' : 'کپی نام کاربری'}
        </Button>

        <Typography variant="subtitle2" gutterBottom>
          توکن دسترسی API
        </Typography>
        <Box
          component="pre"
          dir="ltr"
          style={{ textAlign: 'left' }}
          sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto' }}
        >
          {issued.api_access_token}
        </Box>
        <Button
          size="small"
          sx={{ mt: 1 }}
          onClick={() => void copy(issued.api_access_token, 'token')}
        >
          {copied === 'token' ? 'کپی شد' : 'کپی توکن'}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          کپی کردم، ببند
        </Button>
      </DialogActions>
    </Dialog>
  )
}
